// frontend/src/contexts/ScreenContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';

const ScreenContext = createContext();

export const useScreen = () => {
  const context = useContext(ScreenContext);
  if (!context) {
    throw new Error('useScreen must be used within ScreenProvider');
  }
  return context;
};

// Display Breakpoints
export const BREAKPOINTS = {
  HD: { width: 1920, height: 1080, name: 'HD (1080p)' },
  QHD: { width: 2560, height: 1440, name: '2K (QHD)' },
  UHD: { width: 3840, height: 2160, name: '4K (UHD)' },
};

// Size categories
export const SIZE_CATEGORIES = {
  SMALL: 'small',    // < HD
  MEDIUM: 'medium',  // HD - 2K
  LARGE: 'large',    // 2K - 4K
  XLARGE: 'xlarge'   // >= 4K
};

export const ScreenProvider = ({ children }) => {
  const [screenInfo, setScreenInfo] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
    orientation: 'landscape',
    aspectRatio: 16 / 9,
    category: SIZE_CATEGORIES.MEDIUM,
    breakpoint: 'HD',
    pixelRatio: window.devicePixelRatio || 1
  });

  // Calculate screen category based on resolution
  const getCategory = (width, height) => {
    const pixels = width * height;
    
    if (pixels >= BREAKPOINTS.UHD.width * BREAKPOINTS.UHD.height) {
      return SIZE_CATEGORIES.XLARGE;
    } else if (pixels >= BREAKPOINTS.QHD.width * BREAKPOINTS.QHD.height) {
      return SIZE_CATEGORIES.LARGE;
    } else if (pixels >= BREAKPOINTS.HD.width * BREAKPOINTS.HD.height) {
      return SIZE_CATEGORIES.MEDIUM;
    } else {
      return SIZE_CATEGORIES.SMALL;
    }
  };

  // Get closest breakpoint name
  const getBreakpoint = (width, height) => {
    const pixels = width * height;
    const hdPixels = BREAKPOINTS.HD.width * BREAKPOINTS.HD.height;
    const qhdPixels = BREAKPOINTS.QHD.width * BREAKPOINTS.QHD.height;
    const uhdPixels = BREAKPOINTS.UHD.width * BREAKPOINTS.UHD.height;

    if (Math.abs(pixels - uhdPixels) < Math.abs(pixels - qhdPixels)) {
      return '4K';
    } else if (Math.abs(pixels - qhdPixels) < Math.abs(pixels - hdPixels)) {
      return '2K';
    } else {
      return 'HD';
    }
  };

  // Update screen info
  const updateScreenInfo = () => {
    const width = window.innerWidth;
    const height = window.innerHeight;
    const orientation = width > height ? 'landscape' : 'portrait';
    const aspectRatio = width / height;
    const category = getCategory(width, height);
    const breakpoint = getBreakpoint(width, height);
    const pixelRatio = window.devicePixelRatio || 1;

    setScreenInfo({
      width,
      height,
      orientation,
      aspectRatio,
      category,
      breakpoint,
      pixelRatio
    });
  };

  // Listen to window resize
  useEffect(() => {
    updateScreenInfo();

    const handleResize = () => {
      updateScreenInfo();
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, []);

  // Helper functions for responsive styling
  const helpers = {
    // Get responsive font size multiplier
    getFontSizeMultiplier: () => {
      switch (screenInfo.category) {
        case SIZE_CATEGORIES.XLARGE:
          return 2.0; // 4K+
        case SIZE_CATEGORIES.LARGE:
          return 1.5; // 2K
        case SIZE_CATEGORIES.MEDIUM:
          return 1.0; // HD
        case SIZE_CATEGORIES.SMALL:
          return 0.85; // Below HD
        default:
          return 1.0;
      }
    },

    // Get responsive spacing multiplier
    getSpacingMultiplier: () => {
      switch (screenInfo.category) {
        case SIZE_CATEGORIES.XLARGE:
          return 2.0;
        case SIZE_CATEGORIES.LARGE:
          return 1.5;
        case SIZE_CATEGORIES.MEDIUM:
          return 1.0;
        case SIZE_CATEGORIES.SMALL:
          return 0.9;
        default:
          return 1.0;
      }
    },

    // Check if portrait mode
    isPortrait: () => screenInfo.orientation === 'portrait',

    // Check if landscape mode
    isLandscape: () => screenInfo.orientation === 'landscape',

    // Check if specific category or larger
    isMinCategory: (category) => {
      const order = [SIZE_CATEGORIES.SMALL, SIZE_CATEGORIES.MEDIUM, SIZE_CATEGORIES.LARGE, SIZE_CATEGORIES.XLARGE];
      const currentIndex = order.indexOf(screenInfo.category);
      const targetIndex = order.indexOf(category);
      return currentIndex >= targetIndex;
    },

    // Get responsive class name
    getResponsiveClass: (baseClass) => {
      const suffix = screenInfo.category === SIZE_CATEGORIES.XLARGE ? '-4k' :
                     screenInfo.category === SIZE_CATEGORIES.LARGE ? '-2k' :
                     screenInfo.category === SIZE_CATEGORIES.SMALL ? '-sm' : '';
      return baseClass + suffix;
    }
  };

  const value = {
    ...screenInfo,
    helpers,
    refresh: updateScreenInfo
  };

  return (
    <ScreenContext.Provider value={value}>
      {children}
    </ScreenContext.Provider>
  );
};